import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../services/auth.service';
import { finalize, timeout } from 'rxjs/operators';

type SurveyStep = 'loading' | 'survey' | 'contact' | 'done';
type Nom035Decision = 'clinical_attention_required' | 'no_clinical_attention' | '';

interface Nom035Question {
  id: string;
  text: string;
}

interface Nom035Section {
  id: string;
  title: string;
  officialLabel?: string;
  description?: string;
  questions: Nom035Question[];
}

@Component({
  selector: 'app-nom035-survey',
  templateUrl: './nom035-survey.component.html',
  styleUrls: ['./nom035-survey.component.scss'],
  standalone: false
})
export class Nom035SurveyComponent implements OnInit {
  step: SurveyStep = 'loading';
  requiresAttention = false;
  submittedSurvey = false;
  submittedContact = false;
  isSubmitting = false;
  loadingGuide = true;
  guideError = '';
  errorMessage = '';
  resultMessage = '';
  caseId: number | null = null;
  schemaVersion = 1;
  decision: Nom035Decision = '';
  followUpEnabled = false;
  officialTitle = 'Guía de Referencia I';
  questionnaireTitle = 'CUESTIONARIO PARA IDENTIFICAR A LOS TRABAJADORES QUE FUERON SUJETOS A ACONTECIMIENTOS TRAUMÁTICOS SEVEROS';
  instruction = 'Selecciona la respuesta deseada.';

  sections: Nom035Section[] = [];

  surveyForm: FormGroup = this.fb.group({
    answers: this.fb.group({}),
    first_name: [''],
    last_name: [''],
    email: [''],
    phone: ['']
  });

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadGuide();
  }

  get answersGroup(): FormGroup {
    return this.surveyForm.get('answers') as FormGroup;
  }

  get firstQuestion(): Nom035Question | null {
    return this.sections[0]?.questions?.[0] || null;
  }

  get followUpSections(): Nom035Section[] {
    return this.sections.slice(1);
  }

  get shouldShowFollowUpSections(): boolean {
    return this.followUpEnabled;
  }

  get answeredCount(): number {
    return this.buildAnswersPayload().length;
  }

  get expectedAnswerCount(): number {
    return this.firstQuestion && this.followUpEnabled
      ? this.sections.reduce((total, section) => total + section.questions.length, 0)
      : (this.firstQuestion ? 1 : 0);
  }

  get canContinueSurvey(): boolean {
    return this.expectedAnswerCount > 0 && this.answeredCount === this.expectedAnswerCount;
  }

  get missingAnswersCount(): number {
    return Math.max(this.expectedAnswerCount - this.answeredCount, 0);
  }

  submitSurvey(): void {
    if (this.isSubmitting) {
      return;
    }

    this.submittedSurvey = true;
    this.errorMessage = '';

    if (!this.firstQuestion) {
      this.errorMessage = 'No pudimos cargar el cuestionario. Intenta de nuevo en unos minutos.';
      return;
    }

    const firstQuestionControl = this.answersGroup.get(this.getAnswerControlName(this.firstQuestion.id));
    if (!firstQuestionControl) {
      this.errorMessage = 'No pudimos preparar esta pregunta. Recarga la página e intenta de nuevo.';
      return;
    }

    if (firstQuestionControl.invalid) {
      firstQuestionControl.markAsTouched();
      return;
    }

    const firstAnswerValue = firstQuestionControl.value;
    const firstAnswer = firstAnswerValue === true || firstAnswerValue === 'true';

    if (!firstAnswer) {
      this.requiresAttention = false;
      this.decision = 'no_clinical_attention';
      this.resultMessage = 'Con base en tus respuestas no es necesario generar una sesión en este momento.';
      this.step = 'done';
      this.refreshView();
      return;
    }

    if (!this.followUpEnabled) {
      this.followUpEnabled = true;
      this.setFollowUpValidators(true);
      this.submittedSurvey = false;
      this.refreshView();
      return;
    }

    if (!this.canContinueSurvey || this.answersGroup.invalid) {
      this.answersGroup.markAllAsTouched();
      this.errorMessage = `Responde ${this.missingAnswersCount === 1 ? 'la pregunta pendiente' : `las ${this.missingAnswersCount} preguntas pendientes`} para continuar.`;
      return;
    }

    this.evaluateSurvey();
  }

  onFirstAnswerChange(value: boolean | string): void {
    const isYes = value === true || value === 'true';
    this.followUpEnabled = isYes;
    this.setFollowUpValidators(isYes);

    if (!isYes) {
      this.followUpSections.forEach(section => {
        section.questions.forEach(question => this.answersGroup.get(this.getAnswerControlName(question.id))?.reset());
      });
      this.requiresAttention = false;
      this.decision = 'no_clinical_attention';
      this.resultMessage = 'Con base en tus respuestas no es necesario generar una sesión en este momento.';
    }

    this.refreshView();
  }

  onFollowUpAnswerChange(): void {
    this.errorMessage = '';
  }

  submitContact(): void {
    this.submittedContact = true;

    if (this.surveyForm.invalid) {
      this.surveyForm.markAllAsTouched();
      return;
    }

    this.submitPayload();
  }

  onlyPhoneNumbers(event: Event): void {
    const input = event.target as HTMLInputElement;
    const numbersOnly = input.value.replace(/\D/g, '').slice(0, 15);

    if (input.value !== numbersOnly) {
      input.value = numbersOnly;
      this.surveyForm.get('phone').setValue(numbersOnly, { emitEvent: false });
    }
  }

  restart(): void {
    this.step = 'survey';
    this.requiresAttention = false;
    this.submittedSurvey = false;
    this.submittedContact = false;
    this.isSubmitting = false;
    this.errorMessage = '';
    this.resultMessage = '';
    this.caseId = null;
    this.decision = '';
    this.followUpEnabled = false;
    this.setContactValidators(false);
    this.setFollowUpValidators(false);
    this.surveyForm.patchValue({
      first_name: '',
      last_name: '',
      email: '',
      phone: ''
    });
    Object.keys(this.answersGroup.controls).forEach(key => {
      this.answersGroup.get(key)?.reset();
    });
    this.refreshView();
  }

  isAnswered(questionId: string): boolean {
    const value = this.answersGroup.get(this.getAnswerControlName(questionId))?.value;
    return value === true || value === false;
  }

  getAnswerControlName(questionId: string): string {
    return `q_${String(questionId).replace(/[^a-zA-Z0-9_]/g, '_')}`;
  }

  private loadGuide(): void {
    this.loadingGuide = true;
    this.guideError = '';

    this.authService.getNom035GuideI().subscribe({
      next: (response: any) => {
        try {
          const data = this.parseNom035Response(response);
          this.applyGuide(data);
        } catch (err) {
          console.error('No se pudo procesar la guia NOM035:', err);
          this.sections = [];
          this.guideError = 'No pudimos cargar las preguntas oficiales de la NOM 035. Intenta de nuevo en unos minutos.';
        } finally {
          this.loadingGuide = false;
          this.step = 'survey';
          this.refreshView();
        }
      },
      error: (err) => {
        console.error('No se pudo cargar la guia NOM035:', err);
        this.sections = [];
        this.guideError = 'No pudimos cargar las preguntas oficiales de la NOM 035. Intenta de nuevo en unos minutos.';
        this.loadingGuide = false;
        this.step = 'survey';
        this.refreshView();
      }
    });
  }

  private applyGuide(response: any): void {
    const guide = this.unwrapApiData(response);
    this.schemaVersion = Number(guide?.schema_version || 1);
    this.officialTitle = String(guide?.official_title || this.officialTitle).trim();
    this.questionnaireTitle = String(guide?.questionnaire_title || this.questionnaireTitle).trim();
    this.instruction = this.normalizeInstruction(guide?.instruction || this.instruction);
    const rawSections = guide?.sections || guide?.guide?.sections || [];
    const rawQuestions = guide?.questions || guide?.guide?.questions || [];
    let sections = this.normalizeSections(rawSections);

    if (sections.length === 0 && Array.isArray(rawQuestions)) {
      sections = this.groupQuestionsByOfficialSection(this.normalizeQuestions(rawQuestions, 'I'));
    }

    const questionCount = sections.reduce((total, section) => total + section.questions.length, 0);
    if (sections.length <= 1 && questionCount >= 15) {
      sections = this.groupQuestionsByOfficialSection(sections.reduce((questions, section) => {
        return questions.concat(section.questions);
      }, []));
    }

    if (sections.length === 0 || sections.reduce((total, section) => total + section.questions.length, 0) !== 15) {
      throw new Error('La guia no contiene las 15 preguntas esperadas.');
    }

    this.sections = sections;
    this.rebuildAnswerControls();
  }

  private normalizeSections(sections: any[]): Nom035Section[] {
    return sections.map((section, sectionIndex) => {
      const id = String(section.id || section.section_id || this.getRomanSectionId(sectionIndex)).toUpperCase();
      const questions = this.normalizeQuestions(section.questions || [], id);

      return {
        id,
        title: String(section.title || section.name || this.getFallbackSectionTitle(id)).trim(),
        officialLabel: String(section.official_label || '').trim(),
        description: String(section.description || '').trim(),
        questions
      };
    }).filter(section => section.questions.length > 0);
  }

  private normalizeQuestions(questions: any[], fallbackSectionId: string): Nom035Question[] {
    return questions.map((question, questionIndex) => ({
      id: String(question.id || question.question_id || `${fallbackSectionId}.${questionIndex + 1}`),
      text: String(question.text || question.question || question.label || '').trim()
    })).filter(question => !!question.id && !!question.text);
  }

  private normalizeInstruction(instruction: string): string {
    const value = String(instruction || '').trim();
    if (!value) return 'Selecciona la respuesta deseada.';

    return value.toLowerCase().includes('x')
      ? 'Selecciona la respuesta deseada.'
      : value;
  }

  private groupQuestionsByOfficialSection(questions: Nom035Question[]): Nom035Section[] {
    const sectionIds = ['I', 'II', 'III', 'IV'];

    return sectionIds.map(sectionId => ({
      id: sectionId,
      title: this.getFallbackSectionTitle(sectionId),
      officialLabel: '',
      description: sectionId === 'I' ? '' : 'Durante el último mes',
      questions: questions.filter(question => String(question.id).toUpperCase().startsWith(`${sectionId}.`))
    })).filter(section => section.questions.length > 0);
  }

  private rebuildAnswerControls(): void {
    const controls = {};
    this.sections.forEach((section, sectionIndex) => {
      section.questions.forEach(question => {
        controls[this.getAnswerControlName(question.id)] = sectionIndex === 0 ? ['', Validators.required] : [''];
      });
    });
    this.surveyForm.setControl('answers', this.fb.group(controls));
    this.setFollowUpValidators(false);
  }

  private setFollowUpValidators(required: boolean): void {
    this.followUpSections.forEach(section => {
      section.questions.forEach(question => {
        const control = this.answersGroup.get(this.getAnswerControlName(question.id));
        if (!control) return;

        if (required) {
          control.setValidators([Validators.required]);
        } else {
          control.clearValidators();
        }

        control.updateValueAndValidity();
      });
    });
  }

  private evaluateSurvey(): void {
    const payload = {
      schema_version: this.schemaVersion,
      answers: this.buildAnswersPayload()
    };

    this.setSubmitting(true);
    this.authService.evaluateNom035GuideI(payload).pipe(
      timeout(12000),
      finalize(() => this.setSubmitting(false))
    ).subscribe({
      next: (response: any) => {
        const body = response && response.body ? response.body : response;
        const data = this.parseNom035Response(body);
        const result = this.unwrapApiData(data);
        this.applyEvaluationResult(result);
      },
      error: (err) => {
        this.errorMessage = this.authService.getRequestErrorMessage(err, 'No pudimos evaluar tus respuestas. Intenta de nuevo en unos minutos.');
        this.authService.showRequestError(err, 'No pudimos evaluar tus respuestas. Intenta de nuevo en unos minutos.');
        this.refreshView();
      }
    });
  }

  private applyEvaluationResult(result: any): void {
    const shouldCollectContact = result?.should_collect_contact === true || result?.requires_service === true;
    this.requiresAttention = shouldCollectContact;
    this.decision = shouldCollectContact ? 'clinical_attention_required' : 'no_clinical_attention';

    if (!shouldCollectContact) {
      this.setContactValidators(false);
      this.resultMessage = 'Con base en tus respuestas no es necesario generar una sesión en este momento.';
      this.step = 'done';
      this.refreshView();
      return;
    }

    this.setContactValidators(true);
    this.step = 'contact';
    this.refreshView();
  }

  private setContactValidators(required: boolean): void {
    const firstNameControl = this.surveyForm.get('first_name');
    const lastNameControl = this.surveyForm.get('last_name');
    const emailControl = this.surveyForm.get('email');
    const phoneControl = this.surveyForm.get('phone');

    if (required) {
      firstNameControl.setValidators([Validators.required, Validators.minLength(2)]);
      lastNameControl.setValidators([Validators.required, Validators.minLength(2)]);
      emailControl.setValidators([Validators.required, Validators.email]);
      phoneControl.setValidators([
        Validators.required,
        Validators.pattern(/^[0-9]{10,15}$/)
      ]);
    } else {
      firstNameControl.clearValidators();
      lastNameControl.clearValidators();
      emailControl.clearValidators();
      phoneControl.clearValidators();
    }

    firstNameControl.updateValueAndValidity();
    lastNameControl.updateValueAndValidity();
    emailControl.updateValueAndValidity();
    phoneControl.updateValueAndValidity();
  }

  private submitPayload(): void {
    if (this.isSubmitting) {
      return;
    }

    const firstName = (this.surveyForm.value.first_name || '').trim();
    const lastName = (this.surveyForm.value.last_name || '').trim();
    const payload = {
      schema_version: this.schemaVersion,
      first_name: firstName,
      last_name: lastName,
      email: this.surveyForm.value.email,
      phone: this.surveyForm.value.phone,
      company_id: 2,
      gnp: false,
      answers: this.buildAnswersPayload()
    };

    this.errorMessage = '';
    this.setSubmitting(true);

    this.authService.submitNom035PreRegistration(payload).pipe(
      timeout(12000),
      finalize(() => this.setSubmitting(false))
    ).subscribe({
      next: (response: any) => {
        const body = response && response.body ? response.body : response;
        const data = this.parseNom035Response(body);
        const nomData = this.unwrapApiData(data);
        const result = nomData.result || {};
        const caseData = nomData.case || {};

        this.caseId = caseData.id || nomData.case_id || null;
        this.requiresAttention = result.requires_service === true || caseData.requires_service === true;
        this.resultMessage = 'Gracias por responder. Registramos tu información y el equipo revisará tus respuestas. Pronto se pondrán en contacto contigo.';
        this.step = 'done';
        this.refreshView();
      },
      error: (err) => {
        if (err && err.name === 'TimeoutError') {
          this.resultMessage = 'Recibimos tu solicitud, pero la confirmación tardó más de lo esperado. El equipo revisará tu información para habilitar tu sesión de apoyo.';
          this.requiresAttention = true;
          this.step = 'done';
          this.refreshView();
          return;
        }

        this.errorMessage = this.authService.getRequestErrorMessage(err, 'No pudimos registrar tus respuestas. Intenta de nuevo en unos minutos.');
        this.authService.showRequestError(err, 'No pudimos registrar tus respuestas. Intenta de nuevo en unos minutos.');
        this.refreshView();
      }
    });
  }

  private parseNom035Response(response: any): any {
    if (!response || !response.message) {
      return response || {};
    }

    try {
      return this.authService.parseEncryptedResponse(response, 'public');
    } catch (publicError) {
      try {
        return this.authService.parseEncryptedResponse(response, 'private');
      } catch (privateError) {
        console.warn('No se pudo descifrar respuesta NOM035.', {
          publicError,
          privateError,
          response
        });
        return {};
      }
    }
  }

  private unwrapApiData(data: any): any {
    if (!data) return {};
    if (Array.isArray(data?.data?.items)) return data.data.items[0] || {};
    if (data?.data?.items) return data.data.items;
    if (data?.data) return data.data;
    return data;
  }

  private buildAnswersPayload(): Array<{ question_id: string; answer: boolean }> {
    const answers = this.answersGroup.value || {};
    const payload: Array<{ question_id: string; answer: boolean }> = [];

    this.sections.forEach(section => {
      section.questions.forEach(question => {
        const controlName = this.getAnswerControlName(question.id);
        if (answers[controlName] === true || answers[controlName] === false) {
          payload.push({
            question_id: question.id,
            answer: answers[controlName] === true
          });
        }
      });
    });

    return payload;
  }

  private getRomanSectionId(index: number): string {
    return ['I', 'II', 'III', 'IV'][index] || `${index + 1}`;
  }

  private getFallbackSectionTitle(id: string): string {
    const titles = {
      I: 'I. Acontecimiento traumático severo',
      II: 'II. Recuerdos persistentes sobre el acontecimiento',
      III: 'III. Esfuerzo por evitar circunstancias parecidas o asociadas al acontecimiento',
      IV: 'IV. Afectación'
    };
    return titles[id] || `Sección ${id}`;
  }

  private setSubmitting(value: boolean): void {
    this.isSubmitting = value;
    this.refreshView();
  }

  private refreshView(): void {
    setTimeout(() => this.cdr.detectChanges());
  }
}
